import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  useTheme,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Area,
  Scatter,
  Cell,
} from 'recharts';

const InterventionDiagrams = () => {
  const theme = useTheme();

  // Intervention Effectiveness Data
  const effectivenessData = [
    { month: 'Jan', medication: 75, exercise: 65, environmental: 80 },
    { month: 'Feb', medication: 78, exercise: 70, environmental: 82 },
    { month: 'Mar', medication: 82, exercise: 75, environmental: 85 },
    { month: 'Apr', medication: 85, exercise: 80, environmental: 88 },
    { month: 'May', medication: 88, exercise: 85, environmental: 90 },
    { month: 'Jun', medication: 90, exercise: 88, environmental: 92 },
  ];

  // Exercise Program Impact
  const exerciseData = [
    { week: 'Week 1', strength: 60, balance: 55, adherence: 70 },
    { week: 'Week 2', strength: 65, balance: 60, adherence: 75 },
    { week: 'Week 3', strength: 70, balance: 68, adherence: 80 },
    { week: 'Week 4', strength: 75, balance: 72, adherence: 85 },
    { week: 'Week 5', strength: 80, balance: 78, adherence: 82 },
    { week: 'Week 6', strength: 85, balance: 82, adherence: 88 },
  ];

  // Environmental Modifications Impact
  const environmentalData = [
    { area: 'Bathroom', before: 45, after: 85, incidents: 5 },
    { area: 'Bedroom', before: 55, after: 90, incidents: 3 },
    { area: 'Kitchen', before: 50, after: 88, incidents: 4 },
    { area: 'Living Room', before: 60, after: 92, incidents: 2 },
    { area: 'Stairs', before: 40, after: 85, incidents: 6 },
  ];

  const COLORS = [
    theme.palette.primary.main,
    theme.palette.secondary.main,
    theme.palette.success.main,
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        {/* Intervention Effectiveness */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Intervention Effectiveness Over Time
              </Typography>
              <Typography variant="body1" paragraph>
                This comprehensive analysis tracks the effectiveness of three primary intervention strategies: medication management,
                exercise programs, and environmental modifications. The graph demonstrates the cumulative impact of these
                interventions over a six-month period. Note how different approaches show varying rates of improvement,
                highlighting the importance of a multi-faceted fall prevention strategy.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Key Observations:
                • Medication management shows rapid initial improvement
                • Exercise programs demonstrate steady, progressive benefits
                • Environmental modifications provide consistent risk reduction
                • Combined interventions achieve optimal results
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={effectivenessData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="medication"
                    stroke={COLORS[0]}
                    name="Medication Management"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="exercise"
                    stroke={COLORS[1]}
                    name="Exercise Program"
                    strokeWidth={2}
                  />
                  <Line
                    type="monotone"
                    dataKey="environmental"
                    stroke={COLORS[2]}
                    name="Environmental Modifications"
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Exercise Program Impact */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Exercise Program Progress
              </Typography>
              <Typography variant="body1" paragraph>
                The exercise program combines strength training, balance exercises, and adherence monitoring. This visualization
                shows the relationship between program adherence and improvements in physical capabilities. The data demonstrates
                that consistent participation leads to measurable gains in both strength and balance scores, which are crucial
                factors in fall prevention.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Program Components:
                • Strength training focuses on lower body and core
                • Balance exercises progress from static to dynamic
                • Adherence tracking ensures program effectiveness
                • Weekly assessments guide progression
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={exerciseData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="week" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="adherence"
                    fill={theme.palette.info.light}
                    stroke={theme.palette.info.main}
                    name="Program Adherence"
                  />
                  <Bar
                    dataKey="strength"
                    fill={COLORS[0]}
                    name="Strength Score"
                  />
                  <Line
                    type="monotone"
                    dataKey="balance"
                    stroke={COLORS[1]}
                    name="Balance Score"
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Environmental Modifications Impact */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Environmental Modifications Impact
              </Typography>
              <Typography variant="body1" paragraph>
                Environmental modifications play a crucial role in fall prevention. This analysis compares safety scores before
                and after implementing modifications in different areas of the living space. The scatter points represent reported
                incidents, showing how improved safety scores correlate with reduced fall incidents. Each area presents unique
                challenges and requires specific modification strategies.
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Area-Specific Insights:
                • Bathrooms require the most extensive modifications
                • Bedroom safety focuses on lighting and furniture arrangement
                • Kitchen modifications address common hazards
                • Stairway improvements show significant impact
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={environmentalData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="area" />
                  <YAxis yAxisId="left" domain={[0, 100]} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 10]} />
                  <Tooltip />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="before"
                    fill={theme.palette.warning.light}
                    name="Safety Score Before"
                  />
                  <Bar
                    yAxisId="left"
                    dataKey="after"
                    fill={theme.palette.success.main}
                    name="Safety Score After"
                  />
                  <Scatter
                    yAxisId="right"
                    dataKey="incidents"
                    fill={theme.palette.error.main}
                    name="Incident Reports"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InterventionDiagrams;
